import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export type SecretType = 'string' | 'json' | 'url' | 'number' | 'boolean' | 'ipv4' | 'ipv6';

export interface Secret {
  id: string;
  key: string;
  type: SecretType;
  value?: string;
  description?: string;
  version: number;
  created_at: string;
  updated_at: string;
  inherited_from?: string;
}

export interface SecretWithValue extends Secret {
  value: string;
}

interface SecretsListResponse {
  secrets: Secret[];
}

interface ExportResponse {
  secrets: SecretWithValue[];
}

interface SecretResponse {
  secret: SecretWithValue;
}

interface BulkImportRequest {
  secrets: Array<{
    key: string;
    value: string;
    description?: string;
  }>;
  overwrite?: boolean;
}

interface BulkImportResponse {
  created: number;
  updated: number;
  skipped: number;
}

/**
 * Parse .env file content into key-value pairs
 * Handles:
 * - Standard KEY=value format
 * - Quoted values (single and double quotes)
 * - Escaped characters in double-quoted values (\n, \t, \\, \")
 * - Values containing = after the first occurrence
 * - Comments (lines starting with #)
 * - Inline comments (# preceded by space in unquoted values)
 * - Duplicate keys (last occurrence wins, standard .env behavior)
 */
export function parseEnvContent(content: string): Array<{ key: string; value: string }> {
  const lines = content.split('\n');
  // Use Map to deduplicate keys (last occurrence wins)
  const secretsMap = new Map<string, string>();

  for (const line of lines) {
    // Skip empty lines and comments
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) {
      continue;
    }

    // Parse KEY=VALUE format
    const equalIndex = trimmed.indexOf('=');
    if (equalIndex === -1) {
      continue;
    }

    const key = trimmed.substring(0, equalIndex).trim();
    let value = trimmed.substring(equalIndex + 1);

    // Handle quoted values
    if (value.startsWith('"') && value.endsWith('"') && value.length >= 2) {
      // Double-quoted: process escape sequences
      value = value.slice(1, -1);
      value = value
        .replace(/\\n/g, '\n')
        .replace(/\\t/g, '\t')
        .replace(/\\r/g, '\r')
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\');
    } else if (value.startsWith("'") && value.endsWith("'") && value.length >= 2) {
      // Single-quoted: literal value (no escape processing)
      value = value.slice(1, -1);
    } else {
      // Unquoted: trim whitespace and remove inline comments
      value = value.trim();
      // Remove inline comments (# with optional preceding space)
      // Match " #" or standalone "#" but not inside the value start
      const spaceCommentIndex = value.indexOf(' #');
      const hashIndex = value.indexOf('#');
      if (spaceCommentIndex !== -1) {
        value = value.substring(0, spaceCommentIndex).trim();
      } else if (hashIndex !== -1 && hashIndex > 0) {
        // Also handle "#" without space if it appears later in the value
        value = value.substring(0, hashIndex).trim();
      }
    }

    if (key) {
      secretsMap.set(key, value);
    }
  }

  // Convert Map back to array
  return Array.from(secretsMap.entries()).map(([key, value]) => ({ key, value }));
}

/**
 * Convert secrets array to .env file content
 * Properly escapes special characters in quoted values
 */
export function formatEnvContent(secrets: SecretWithValue[]): string {
  return secrets
    .map(({ key, value }) => {
      // Quote values that contain spaces, newlines, or special chars
      const needsQuotes = /[\s#=]/.test(value) || value.includes('\n') || value.includes('\r') || value.includes('\t');
      if (needsQuotes) {
        // Escape special characters for double-quoted values
        const escapedValue = value
          .replace(/\\/g, '\\\\')  // Escape backslashes first
          .replace(/"/g, '\\"')    // Escape double quotes
          .replace(/\n/g, '\\n')   // Escape newlines
          .replace(/\r/g, '\\r')   // Escape carriage returns
          .replace(/\t/g, '\\t');  // Escape tabs
        return `${key}="${escapedValue}"`;
      }
      return `${key}=${value}`;
    })
    .join('\n');
}

/**
 * Hook for managing secrets in a project environment
 */
export function useSecrets(projectId: string | null, environmentSlug: string | null) {
  const queryClient = useQueryClient();
  const basePath = projectId && environmentSlug
    ? `/api/v1/projects/${projectId}/environments/${environmentSlug}/secrets`
    : null;

  // Fetch secrets list (keys only)
  const secretsQuery = useQuery({
    queryKey: ['secrets', projectId, environmentSlug],
    queryFn: async () => {
      if (!basePath) return [];
      const response = await apiClient.get<SecretsListResponse>(basePath);
      return response.data.secrets;
    },
    enabled: !!basePath,
    staleTime: 1000 * 60, // 1 minute
  });

  // Get single secret with value
  const getSecretMutation = useMutation({
    mutationFn: async (key: string) => {
      if (!basePath) return Promise.reject(new Error('Please select a project and environment first'));
      const response = await apiClient.get<SecretResponse>(`${basePath}/${encodeURIComponent(key)}`);
      return response.data.secret;
    },
  });

  // Export all secrets with values
  const exportSecretsMutation = useMutation({
    mutationFn: async () => {
      if (!basePath) return Promise.reject(new Error('Please select a project and environment first'));
      const response = await apiClient.get<ExportResponse>(`${basePath}/export`);
      return response.data.secrets;
    },
  });

  // Create secret
  const createSecretMutation = useMutation({
    mutationFn: async (data: { key: string; value: string; description?: string }) => {
      if (!basePath) return Promise.reject(new Error('Please select a project and environment first'));
      const response = await apiClient.post<SecretResponse>(basePath, data);
      return response.data.secret;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['secrets', projectId, environmentSlug] });
    },
  });

  // Update secret
  const updateSecretMutation = useMutation({
    mutationFn: async ({ key, ...data }: { key: string; value?: string; description?: string }) => {
      if (!basePath) return Promise.reject(new Error('Please select a project and environment first'));
      const response = await apiClient.put<SecretResponse>(`${basePath}/${encodeURIComponent(key)}`, data);
      return response.data.secret;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['secrets', projectId, environmentSlug] });
    },
  });

  // Delete secret (or hide inherited secret)
  const deleteSecretMutation = useMutation({
    mutationFn: async (key: string) => {
      if (!basePath) return Promise.reject(new Error('Please select a project and environment first'));
      await apiClient.delete(`${basePath}/${encodeURIComponent(key)}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['secrets', projectId, environmentSlug] });
    },
  });

  // Restore hidden inherited secret
  const restoreSecretMutation = useMutation({
    mutationFn: async (key: string) => {
      if (!basePath) return Promise.reject(new Error('Please select a project and environment first'));
      await apiClient.post(`${basePath}/${encodeURIComponent(key)}/restore`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['secrets', projectId, environmentSlug] });
    },
  });

  // Bulk import secrets
  const bulkImportMutation = useMutation({
    mutationFn: async (data: BulkImportRequest) => {
      if (!basePath) return Promise.reject(new Error('Please select a project and environment first'));
      const response = await apiClient.post<BulkImportResponse>(`${basePath}/bulk`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['secrets', projectId, environmentSlug] });
    },
  });

  // Helper to download secrets as .env file
  const downloadEnvFile = async (filename: string = '.env') => {
    const secrets = await exportSecretsMutation.mutateAsync();
    const content = formatEnvContent(secrets);

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return {
    // Secrets list
    secrets: secretsQuery.data ?? [],
    isLoading: secretsQuery.isLoading,
    error: secretsQuery.error,
    refetch: secretsQuery.refetch,

    // Get single secret
    getSecret: getSecretMutation.mutateAsync,
    isGettingSecret: getSecretMutation.isPending,

    // Export
    exportSecrets: exportSecretsMutation.mutateAsync,
    isExporting: exportSecretsMutation.isPending,
    downloadEnvFile,

    // Create
    createSecret: createSecretMutation.mutateAsync,
    isCreating: createSecretMutation.isPending,
    createError: createSecretMutation.error,

    // Update
    updateSecret: updateSecretMutation.mutateAsync,
    isUpdating: updateSecretMutation.isPending,
    updateError: updateSecretMutation.error,

    // Delete (or hide inherited)
    deleteSecret: deleteSecretMutation.mutateAsync,
    isDeleting: deleteSecretMutation.isPending,
    deleteError: deleteSecretMutation.error,

    // Restore hidden inherited secret
    restoreSecret: restoreSecretMutation.mutateAsync,
    isRestoring: restoreSecretMutation.isPending,
    restoreError: restoreSecretMutation.error,

    // Bulk import
    bulkImport: bulkImportMutation.mutateAsync,
    isBulkImporting: bulkImportMutation.isPending,
    bulkImportError: bulkImportMutation.error,
  };
}
